import { useState } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Card from "../shared/Card/Card";
import MarketplacePills from "../shared/MarketplacePills";
import NotesOutput from "../shared/Notes/NotesOutput";
import PartConditionPickerButton from "../shared/PartConditionPicker/PartConditionPickerButton";
import PartMarketplaces from "../parts/PartMarketplaces";
import ListingStatus from "./ListingStatus";
import PromotionPicker from "./PromotionPicker";
import { VehicleCategoryParts } from "./VehicleCategoryParts";
import useEbayPromotion from "../../data/useEbayPromotion";
import { FormatType, IEbayOffer, ListingDuration } from "../../models/EbayOffer";
import { ResourceType } from "../../models/Note";
import { IPart } from "../../models/Part";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";

function ItemInfo({ title, value, cols = "col-span-1" }: { title: string, value: string, cols?: string }) {
  return (
    <div className={`${cols} flex flex-col`}>
      <p className="text-sm text-gray-500">{title}</p>
      <p className="text-lg">{value}</p>
    </div>
  )
}

function SingleListing({ offer, part, setShowPromotionPicker }: { offer: IEbayOffer, part: IPart, setShowPromotionPicker: React.Dispatch<React.SetStateAction<boolean>> }) {
  const { t } = useTranslation("stock");
  const { promotion } = useEbayPromotion(part.promotionId ? part.promotionId : "");

  const p = promotion as any;
  const isActiveOffer = part.activePartOfferId ? part.activePartOfferId === offer.id : part.offers[0]?.id === offer.id;

  return (
    <div className="p-5">
      <div className="grid grid-cols-2 gap-y-2">
        <ItemInfo title={t("Title")} value={offer.title} />

        <div className="w-full flex flex-col justify-center items-center">
          <ListingStatus offer={offer} part={part} width="w-3/4" />
          {/* The status above is eBay's. Where else the part is live, failed or still going up sits
              under it - only on the active offer, since the rows belong to the part, not the offer. */}
          {isActiveOffer && <MarketplacePills part={part} />}
        </div>

        <ItemInfo title={t("Listing Start")}
          value={offer.listingStarted ? formatDateTime(offer.listingStarted, DateTime.DATETIME_MED) : t("Not listed")} />
        <ItemInfo title={t("Type")} value={FormatType[offer.format]} />
        <ItemInfo title={t("Price")}
          value={PriceFormat(offer.format == FormatType.Auction ? offer.auctionStartPrice : offer.buyItNowPrice)} />
        <ItemInfo title={t("Duration")} value={ListingDuration[offer.duration]} />

        <div className={`flex flex-col gap-y-1 col-span-2`}>
          <p className="text-sm text-gray-500">{t("Promotion")}</p>
          <div className="flex gap-2 items-center">
            <p className="text-lg">{part.promotionId && p.data?.data?.response ? p.data?.data?.response?.name : t("None")}</p>
            {
              part.promotionId == null && offer.publishedOn &&
              <FontAwesomeIcon className="rounded-full p-1 cursor-pointer hover:ring-2 hover:ring-blue-500" icon={faPencil}
                onClick={() => setShowPromotionPicker(true)}
              />
            }
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * The listing-specific cards that used to live in the right column of the standalone
 * eBay listing page. Rendered alongside the stock cards while the Listing tab is active.
 */
export default function ListingSidebar({ part }: { part: IPart }) {
  const { t } = useTranslation("stock");
  const [showPromotionPicker, setShowPromotionPicker] = useState<boolean>(false);

  return (
    <>
      <PromotionPicker open={showPromotionPicker} setOpen={setShowPromotionPicker} part={part} />

      <Card title={t("Listings")} bodyClassName="flex flex-col divide-y">
        {part.offers.length === 0 ? (
          <p className="p-5 text-sm text-gray-500">{t("No listings for this part yet.")}</p>
        ) : (
          part.offers.map((offer) => (
            <SingleListing key={offer.id} offer={offer} part={part} setShowPromotionPicker={setShowPromotionPicker} />
          ))
        )}
      </Card>

      <Card
        title={t("Part Condition Notes")}
        subtitle={t("This will go onto the listing")}
        bodyClassName="px-2 pb-4"
        headerRight={<PartConditionPickerButton resourceId={part.id} resourceType={ResourceType.Part} />}
      >
        <div>
          <NotesOutput
            resourceId={part.id}
            resourceType={ResourceType.Part}
            shouldScroll={false}
          />
        </div>
      </Card>

      <Card title={t("Internal Part Notes")} subtitle={t("This will not go onto the listing")} bodyClassName="px-2 pb-4">
        <div>
          <NotesOutput
            resourceId={part.id}
            resourceType={ResourceType.InternalPart}
            shouldScroll={false}
          />
        </div>
      </Card>

      <VehicleCategoryParts vehicleId={part.vehicleId!} categoryId={part.categoryId!} />

      <PartMarketplaces part={part} />
    </>
  )
}
